import { useQuery } from "@tanstack/react-query"
import {
	type FlowTriggers,
	NODE_TYPE_LABELS,
	type NodeType,
	parseTriggers,
	summarizeNode,
} from "@velizhanin-dev/bot-scenario"
import { Copy, Pencil, Plus, Search, SquarePen, Star, Trash2, Workflow, Zap } from "lucide-react"
import { useMemo, useState } from "react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import type { DraftFlow, DraftNode } from "@/entities/bot-builder"
import { getFlowStats, statsKeys } from "@/entities/bot-stats"
import { FlowTriggersDialog } from "@/features/edit-scenario-draft"
import { KIND_ORDER, NameDialog } from "@/features/manage-flows"
import { UtmFilter } from "@/features/manage-utm-links"
import { Badge } from "@/shared/ui/badge"
import { Button } from "@/shared/ui/button"
import { useConfirm } from "@/shared/ui/confirm-provider"
import { EmptyState } from "@/shared/ui/empty-state"
import { Input } from "@/shared/ui/input"
import { RowActionsMenu } from "@/shared/ui/row-actions-menu"
import { Switch } from "@/shared/ui/switch"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/shared/ui/table"
import {
	botPath,
	type LoadedDraft,
	percent,
	useBotWorkspace,
	WithDraft,
	WorkspaceSection,
	FunnelsHelp,
} from "@/widgets/bot-workspace"
import { AutoHeight } from "@/shared/ui/auto-height"
import { LoadSwap } from "@/shared/ui/load-swap"

/** Как редактор воронки открывает панель людей на блоке (см. FunnelEditorState). */
interface BlockPeopleState {
	nodeId: string
	mode: "passed" | "stuck"
	label: string
}

/** «Конструктор → Воронки»: включение, основная, статистика и переход в редактор. */
export function FunnelsPage() {
	return (
		<WithDraft
			title="Воронки"
			subtitle="Цепочки сообщений. Основная воронка запускается при входе в бота, по команде /start."
		>
			{(state) => <FunnelsTable state={state} />}
		</WithDraft>
	)
}

function FunnelsTable({ state }: { state: LoadedDraft }) {
	const { bot } = useBotWorkspace()
	const navigate = useNavigate()
	const { confirm } = useConfirm()
	const { draft, actions } = state
	const [search, setSearch] = useState("")
	const [creating, setCreating] = useState(false)
	const [renaming, setRenaming] = useState<DraftFlow | null>(null)
	const [triggersFlow, setTriggersFlow] = useState<DraftFlow | null>(null)

	const [searchParams, setSearchParams] = useSearchParams()
	const utm = searchParams.get("utm") ?? ""
	const setUtm = (code: string) =>
		setSearchParams(
			(current) => {
				const next = new URLSearchParams(current)
				if (code) next.set("utm", code)
				else next.delete("utm")
				return next
			},
			{ replace: true },
		)

	const stats = useQuery({
		queryKey: statsKeys.flows(bot.id, utm),
		queryFn: () => getFlowStats(bot.id, utm || undefined),
		refetchInterval: 60_000,
	})
	const statsByFlow = useMemo(
		() => new Map((stats.data?.flows ?? []).map((row) => [row.flowId, row])),
		[stats.data],
	)
	// Где воронка теряет больше всего людей: блок с наибольшим числом застрявших. Ожидание
	// не в счёт, там люди стоят по задумке.
	const worstByFlow = useMemo(() => {
		const nodes = new Map(draft.nodes.map((node) => [node.id, node]))
		const worst = new Map<string, { node: DraftNode; stuck: number }>()
		for (const row of stats.data?.nodes ?? []) {
			const node = nodes.get(row.nodeId)
			if (!node || node.type === "wait" || row.stuck <= 0) continue
			const current = worst.get(node.flowId)
			if (!current || row.stuck > current.stuck) worst.set(node.flowId, { node, stuck: row.stuck })
		}
		return worst
	}, [draft.nodes, stats.data])
	const linksByFlow = useMemo(() => {
		const counts = new Map<string, number>()
		for (const link of draft.entryLinks ?? [])
			if (link.flowId) counts.set(link.flowId, (counts.get(link.flowId) ?? 0) + 1)
		return counts
	}, [draft.entryLinks])

	const query = search.trim().toLocaleLowerCase()
	const flows = [...draft.flows]
		.filter((flow) => !query || flow.name.toLocaleLowerCase().includes(query))
		.sort((a, b) => KIND_ORDER[a.kind] - KIND_ORDER[b.kind] || a.sortOrder - b.sortOrder)

	const openFlow = (flowId: string, state?: { focusNodeId: string; people: BlockPeopleState }) =>
		navigate(
			{
				pathname: botPath(bot.id, `constructor/funnels/${flowId}`),
				search: utm ? `?utm=${encodeURIComponent(utm)}` : "",
			},
			{ state },
		)

	return (
		<WorkspaceSection
			title="Воронки"
			subtitle="Цепочки сообщений. Основная воронка запускается при входе в бота, по команде /start. Выключенная воронка не запускается ни ссылками, ни меню."
			help={<FunnelsHelp />}
			actions={
				<Button className="rounded-xl" onClick={() => setCreating(true)}>
					<Plus />
					Добавить воронку
				</Button>
			}
		>
			<div className="mb-3 flex flex-col gap-2 sm:flex-row">
				<div className="relative flex-1">
					<Search className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
					<Input
						aria-label="Поиск воронки"
						placeholder="Найти воронку"
						className="h-10 pl-9"
						value={search}
						onChange={(event) => setSearch(event.target.value)}
					/>
				</div>
				<UtmFilter
					links={draft.entryLinks ?? []}
					value={utm}
					onChange={setUtm}
					className="h-10 sm:w-72"
				/>
			</div>

			{/* «Пусто» и список сменяют друг друга перекрёстно, высота тянется к новой. */}
			<LoadSwap loading={false} id={flows.length === 0 ? "empty" : "list"}>
				{flows.length === 0 ? (
					<EmptyState icon={Workflow} title="Ничего не нашлось" />
				) : (
					<AutoHeight className="overflow-x-auto rounded-2xl border border-border bg-card">
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead className="w-20">Активна</TableHead>
									<TableHead className="hidden w-24 sm:table-cell">Основная</TableHead>
									<TableHead>Название</TableHead>
									<TableHead className="hidden w-56 md:table-cell">Статистика</TableHead>
									<TableHead className="w-10" />
								</TableRow>
							</TableHeader>
							<TableBody>
								{flows.map((flow) => {
									const triggers: FlowTriggers = parseTriggers(flow.triggers)
									const flowStats = statsByFlow.get(flow.id)
									const started = flowStats?.started ?? 0
									const finished = flowStats?.finished ?? 0
									const links = linksByFlow.get(flow.id) ?? 0
									const active = flow.active !== false
									const worst = worstByFlow.get(flow.id)
									const worstLabel = worst
										? summarizeNode(worst.node, draft.flows, draft.tags)
												.replace(/\s+/g, " ")
												.slice(0, 40) || NODE_TYPE_LABELS[worst.node.type as NodeType]
										: ""
									const openWorst = () =>
										worst &&
										openFlow(flow.id, {
											focusNodeId: worst.node.id,
											people: { nodeId: worst.node.id, mode: "stuck", label: worstLabel },
										})
									const canBeMain = flow.kind === "CUSTOM" && !triggers.background
									const askMakeMain = () =>
										confirm({
											title: `Сделать «${flow.name}» основной?`,
											message:
												"С неё начнут все, кто нажмёт /start без метки. Прежняя основная станет обычной воронкой.",
											confirmText: "Сделать основной",
											onConfirm: async () => {
												await actions.makeFlowMain(flow.id)
											},
										})
									return (
										<TableRow key={flow.id} className={active ? undefined : "opacity-60"}>
											<TableCell>
												<Switch
													aria-label={`Воронка «${flow.name}» включена`}
													checked={active}
													disabled={flow.kind !== "CUSTOM"}
													onCheckedChange={(checked) =>
														void actions.setFlowActive(flow.id, checked)
													}
												/>
											</TableCell>
											<TableCell className="hidden sm:table-cell">
												<Switch
													aria-label={`Воронка «${flow.name}» основная`}
													checked={flow.kind === "MAIN"}
													disabled={!canBeMain}
													onCheckedChange={(checked) => {
														if (checked) askMakeMain()
													}}
												/>
											</TableCell>
											<TableCell>
												<Link
													to={{
														pathname: botPath(bot.id, `constructor/funnels/${flow.id}`),
														search: utm ? `?utm=${encodeURIComponent(utm)}` : "",
													}}
													className="font-medium hover:underline"
												>
													{flow.name}
												</Link>
												<div className="mt-1 flex flex-wrap gap-1">
													{flow.kind === "FALLBACK" ? (
														<Badge variant="secondary">Не понял</Badge>
													) : null}
													{triggers.background ? <Badge variant="secondary">фоновая</Badge> : null}
													{links > 0 ? (
														<Badge variant="outline">
															{links} {links === 1 ? "метка" : "меток"}
														</Badge>
													) : null}
													{triggers.commands.map((item) => (
														<Badge key={item.command} variant="outline">
															/{item.command}
														</Badge>
													))}
													{triggers.events.map((item) => (
														<Badge key={item.code} variant="outline">
															{item.code}
														</Badge>
													))}
												</div>
												<p className="mt-1 text-xs text-muted-foreground tabular-nums md:hidden">
													{flow.kind === "MAIN" ? "Основная · " : ""}
													{started} → {finished} · {percent(finished, started)}
												</p>
												{worst ? (
													<button
														type="button"
														onClick={openWorst}
														className="mt-1 block max-w-full truncate text-left text-xs text-amber-300 md:hidden"
													>
														Застревают: «{worstLabel}», {worst.stuck}
													</button>
												) : null}
											</TableCell>
											<TableCell className="hidden text-xs text-muted-foreground md:table-cell">
												<span className="tabular-nums">
													Начали: <b className="text-foreground">{started}</b> Завершили:{" "}
													<b className="text-foreground">{finished}</b>
												</span>
												<br />
												Эффективность: {percent(finished, started)}
												{worst ? (
													<button
														type="button"
														onClick={openWorst}
														title="Открыть блок и людей, которые на нём застряли"
														className="mt-1 block max-w-full truncate text-left text-amber-300 hover:underline"
													>
														Застревают: «{worstLabel}», {worst.stuck}
													</button>
												) : null}
											</TableCell>
											<TableCell>
												<RowActionsMenu
													label={`Действия с «${flow.name}»`}
													actions={[
														{
															label: "Открыть",
															icon: SquarePen,
															onSelect: () => openFlow(flow.id),
														},
														{
															label: "Запуск: команды и события",
															icon: Zap,
															hidden: flow.kind === "FALLBACK",
															onSelect: () => setTriggersFlow(flow),
														},
														{
															label: "Дублировать",
															icon: Copy,
															onSelect: async () => {
																const result = await actions.duplicateFlow(flow.id)
																if (result) openFlow(result.flow.id)
															},
														},
														{
															label: "Переименовать",
															icon: Pencil,
															onSelect: () => setRenaming(flow),
														},
														{
															label: "Сделать основной",
															icon: Star,
															hidden: !canBeMain,
															onSelect: askMakeMain,
														},
														{
															label: "Удалить",
															icon: Trash2,
															destructive: true,
															separatorBefore: true,
															hidden: flow.kind !== "CUSTOM",
															onSelect: () =>
																confirm({
																	title: `Удалить «${flow.name}»?`,
																	message:
																		links > 0
																			? `На воронку ведут UTM-метки (${links}), после удаления они поведут в основную.`
																			: "Воронка удалится вместе с блоками.",
																	confirmText: "Удалить",
																	onConfirm: async () => {
																		await actions.deleteFlow(flow.id)
																	},
																}),
														},
													]}
												/>
											</TableCell>
										</TableRow>
									)
								})}
							</TableBody>
						</Table>
					</AutoHeight>
				)}
			</LoadSwap>

			<NameDialog
				title="Новая воронка"
				initial=""
				open={creating}
				onOpenChange={setCreating}
				onSubmit={async (name) => {
					const result = await actions.createFlow(name)
					setCreating(false)
					if (result) openFlow(result.flow.id)
				}}
			/>
			<NameDialog
				title="Переименовать воронку"
				initial={renaming?.name ?? ""}
				open={!!renaming}
				onOpenChange={(open) => {
					if (!open) setRenaming(null)
				}}
				onSubmit={async (name) => {
					if (renaming) await actions.renameFlow(renaming.id, name)
					setRenaming(null)
				}}
			/>
			<FlowTriggersDialog
				flow={triggersFlow}
				onOpenChange={(open) => {
					if (!open) setTriggersFlow(null)
				}}
				onSave={(flowId, triggers) => actions.setFlowTriggers(flowId, triggers)}
			/>
		</WorkspaceSection>
	)
}
